import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { toggleModal, modalState, detailOpenState } from 'reducers/ui';
import { useDispatch, useSelector } from 'react-redux';
import staticText from 'static/text_display.json';
import useBusinessClients from 'containers/Pages/People/BusinessClients/Layouts/useBusinessClient';
import useFilters from 'containers/Base/useFilters.js';
import { getUserAssignedDepartmentList } from 'reducers/users';
import LayoutWithMap from 'containers/Base/LayoutWithMap';
import { PAGE_URLS } from 'utils/constants';
import {
  siteDataGridActions,
  siteFilterActions,
  sitesNS,
  siteSelectors,
} from 'containers/Pages/Locations/Sites';

const BaseSitePanel = ({
  editable,
  columns = [],
  filters,
  SiteDetailComponent,
  loading,
  SiteFormComponent,
}) => {
  const [siteGridApi, setSiteGridApi] = useState();

  //---------------------Selectors------------------------------
  const onMapSelectedSiteId = useSelector(state =>
    siteSelectors.getSelectedRowID(state),
  );
  const sites = useSelector(state =>
    siteSelectors.getSiteListTransformed(state),
  );
  const modalOpen = useSelector(state => modalState(state));
  const filterValues = useSelector(state => siteSelectors.getFilters(state));
  const totalEntries = useSelector(state =>
    siteSelectors.getTotalEntries(state),
  );
  const forceRefresh = useSelector(state =>
    siteSelectors.getForceRefresh(state),
  );

  const userAssignedDepartments = useSelector(state =>
    getUserAssignedDepartmentList(state),
  );
  const detailOpen = useSelector(state => detailOpenState(state));
  //-----------------------------Custom hooks------------------
  const bizClients = useBusinessClients();
  const { localFilterValues, updateFilter } = useFilters({
    filterValues,
  });

  //-------------------Actions---------------------------------
  const dispatch = useDispatch();

  const dispatchSetFilters = useCallback(
    filters => dispatch(siteFilterActions.setFilters(filters)),
    [dispatch],
  );

  const dispatchSetSiteFilterKey = useCallback(
    key => dispatch(siteFilterActions.setFilterKey(key)),
    [dispatch],
  );

  const dispatchFetchSitesList = useCallback(
    ({ filters, from }) =>
      dispatch(
        siteDataGridActions.fetchGridList({
          filters,
          from,
        }),
      ),
    [dispatch],
  );
  const dispatchToggleModal = useCallback(
    () => dispatch(toggleModal(sitesNS)),
    [dispatch],
  );

  //-----------------------------------------------------------

  const onUpdate = filters => {
    dispatchFetchSitesList({
      filters,
    });
  };

  const fetchSites = from => {
    dispatchFetchSitesList({
      filters: localFilterValues,
      from,
    });
  };

  const gridAPIHandler = gridAPI => {
    setSiteGridApi(gridAPI);
  };

  const siteHighlight = onMapSelectedSiteId => {
    if (onMapSelectedSiteId != null) {
      const node = siteGridApi && siteGridApi.getRowNode(onMapSelectedSiteId);
      // Go to the highlighted node
      siteGridApi && siteGridApi.ensureNodeVisible(node, { position: 'top' });
      if (node) {
        if (!node.isSelected()) {
          node.setSelected(true);
        }
      }
    }
  };
  siteHighlight(onMapSelectedSiteId);

  const enhancedCancellation = () => {
    dispatch(siteDataGridActions.setGridRowDetails({ details: {} }));
  };

  return (
    <LayoutWithMap
      header="Sites"
      toggleModal={dispatchToggleModal}
      cols={columns}
      data={sites}
      loading={loading}
      onEdit={modalOpen[sitesNS]}
      editable={editable}
      DetailComponent={SiteDetailComponent}
      FormComponent={SiteFormComponent}
      filters={filters({
        bizClients,
        userAssignedDepartments,
      })}
      filterValues={localFilterValues}
      filtersRequired={true}
      setFilters={(values, hasApplyFilter) =>
        updateFilter({
          values,
          hasApplyFilter,
          applyFilter: vals => {
            dispatchSetSiteFilterKey(true);
            dispatchSetFilters(vals);
          },
        })
      }
      onUpdate={onUpdate}
      fetchData={fetchSites}
      forceRefresh={forceRefresh}
      totalEntries={totalEntries}
      createTextHover={staticText.managerial_view.site.create}
      setGridAPI={gridAPIHandler}
      enhancedCancellation={enhancedCancellation}
      persistentStateName={PAGE_URLS.SITES.name}
      forceOpenDetailPanel={detailOpen[PAGE_URLS.SITES.link]}
    />
  );
};

BaseSitePanel.propTypes = {
  editable: PropTypes.bool,
  columns: PropTypes.array,
  SiteDetailComponent: PropTypes.elementType,
  SiteFormComponent: PropTypes.elementType,
  filters: PropTypes.func,
  loading: PropTypes.bool,
};

BaseSitePanel.defaultProps = {
  editable: true,
};

export default BaseSitePanel;
